import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {catchError, firstValueFrom, map, Observable, of} from 'rxjs';
import {LocalStorageService} from '../local-storage.service';
import {MessageBoxService} from './message-box.service';
import {HelperService} from './helper.service';
import {UserAuthenticationService} from './user-authentication.service';
import {BACKUP_ENDPOINT} from '../models/backend-api-routes';

// Metadata about the backup currently stored on the server for the authenticated user.
export interface ServerBackupInfo {
    available: boolean;
    createdAt?: string; // ISO-8601 timestamp of when the backup was taken
}

@Injectable({
    providedIn: 'root',
})
export class BackupService {
    backUpFileTypes = ['vinbkup'];

    private localStorageService = inject(LocalStorageService);
    private messageBoxService = inject(MessageBoxService);
    private helperService = inject(HelperService);
    private userAuthService = inject(UserAuthenticationService);
    private http = inject(HttpClient);

    async downloadBackUp(): Promise<void> {
        this.helperService.lockApp();
        const dataStr = await this.createBackup();
        const dlAnchorElem = document.createElement('a');
        if (dlAnchorElem) {
            dlAnchorElem.setAttribute('href', dataStr);
            dlAnchorElem.setAttribute('download', 'bkup.vinbkup');
            dlAnchorElem.click();
        }
        this.helperService.unlockApp();
    }

    async createBackup(): Promise<string> {
        const jsonString = await this.getEncryptedBackupString();
        return 'data:text/vinbkup;charset=utf-8,' + encodeURIComponent(jsonString);
    }

    // Produces the same encrypted content as createBackup(), but as a File so it can be
    // uploaded directly and later re-read by loadBackup() without any format conversion.
    async createBackupFile(fileName: string = 'bkup.vinbkup'): Promise<File> {
        const jsonString = await this.getEncryptedBackupString();
        return new File([jsonString], fileName, {type: 'text/vinbkup'});
    }

    private async getEncryptedBackupString(): Promise<string> {
        const obj = await this.localStorageService.getAllData();
        return this.helperService.encryptUsingAES256(JSON.stringify(obj));
    }

    uploadBackupToServer(file: File): Observable<boolean> {
        const formData = new FormData();
        formData.append('file', file);

        return this.http.post(`${this.getBackupUrl()}/UploadBackup`, formData, {headers: this.getAuthHeaders()})
            .pipe(map(() => true), catchError(() => of(false)));
    }

    async getServerBackupInfo(): Promise<ServerBackupInfo | undefined> {
        try {
            return await firstValueFrom(this.http.get<ServerBackupInfo>(`${this.getBackupUrl()}/BackupInfo`, {headers: this.getAuthHeaders()}));
        } catch {
            return undefined;
        }
    }

    async downloadServerBackup(): Promise<void> {
        this.helperService.lockApp();
        try {
            const encrypted = await this.fetchServerBackup();
            const dataStr = 'data:text/vinbkup;charset=utf-8,' + encodeURIComponent(encrypted);
            const dlAnchorElem = document.createElement('a');
            dlAnchorElem.setAttribute('href', dataStr);
            dlAnchorElem.setAttribute('download', 'server-bkup.vinbkup');
            dlAnchorElem.click();
        } catch (ex) {
            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_DIALOG.ERROR_DIALOG.ERROR_SERVER_BACKUP.text', 'MAIN_APP_DIALOG.ERROR_DIALOG.ERROR_SERVER_BACKUP.title');
        } finally {
            this.helperService.unlockApp();
        }
    }

    async restoreServerBackup(): Promise<void> {
        const confirmed = await this.messageBoxService.askYesNoQuestion(
            'MAIN_APP_DIALOG.APPLY_BACKUP.msg',
            'MAIN_APP_DIALOG.APPLY_BACKUP.title'
        );
        if (!confirmed) {
            return;
        }

        this.helperService.lockApp();
        try {
            const encrypted = await this.fetchServerBackup();
            const s = this.helperService.decryptUsingAES256(encrypted);
            const obj = JSON.parse(JSON.parse(s));
            await this.applyBackup(obj);
        } catch (ex) {
            this.helperService.unlockApp();
            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_DIALOG.ERROR_DIALOG.ERROR_SERVER_BACKUP.text', 'MAIN_APP_DIALOG.ERROR_DIALOG.ERROR_SERVER_BACKUP.title');
        }
    }

    private fetchServerBackup(): Promise<string> {
        return firstValueFrom(this.http.get(`${this.getBackupUrl()}/DownloadBackup`, {
            headers: this.getAuthHeaders(),
            responseType: 'text'
        }));
    }

    private getBackupUrl(): string {
        return BACKUP_ENDPOINT;
    }

    private getAuthHeaders(): HttpHeaders {
        return new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo)
        });
    }

    loadBackup(vinbkInput: any): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                const fl: any = vinbkInput?.nativeElement?.files;
                if (fl && fl.length === 1) {
                    this.messageBoxService.askYesNoQuestion(
                        'MAIN_APP_DIALOG.APPLY_BACKUP.msg',
                        'MAIN_APP_DIALOG.APPLY_BACKUP.title'
                    ).then(res => {
                        if (!res) {
                            resolve();
                            return;
                        }
                        this.helperService.lockApp();
                        const fr = new FileReader();
                        fr.addEventListener('load', async () => {
                            const s = this.helperService.decryptUsingAES256(fr.result as string);
                            const obj = JSON.parse(JSON.parse(s));
                            await this.applyBackup(obj);
                            resolve();
                        });
                        fr.readAsText(fl[0], 'utf8');
                    });
                } else {
                    vinbkInput.nativeElement.click();
                }
            } catch (ex) {
                this.helperService.unlockApp();
                reject(ex);
            }
        });
    }

    acceptableFile(file: File | DataTransferItem): boolean {
        let type: string;
        if (file instanceof File) {
            const split = (file as any).name.split('.');
            type = split[split.length - 1];
        } else {
            type = file.type;
        }
        for (const item of this.backUpFileTypes) {
            if (type == item) {
                return true;
            }
        }
        return false;
    }


    async applyBackup(obj: any): Promise<void> {
        this.localStorageService.lockDb();
        try {
            await this.localStorageService.resetLocalStorage();
            await this.restoreObjects(obj);
        } finally {
            this.localStorageService.unlockDB();
            window.location.reload();
        }
    }


    private async restoreObjects(obj: any): Promise<any> {
        const p: any[] = [];
        const names: any = Object.getOwnPropertyNames(obj);
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < names.length; i++) {
            const keys: any = Object.getOwnPropertyNames(obj[names[i]]);
            // tslint:disable-next-line:prefer-for-of
            for (let j = 0; j < keys.length; j++) {
                p.push(this.localStorageService.add(keys[j], obj[names[i]][keys[j]], names[i], true, true));
            }
        }
        return await Promise.all(p);
    }
}
